import { useUI } from '@/context/ui'
import dayjs from 'dayjs'
import { useEffect, useMemo, useRef, useState } from 'react'

interface Props {
  isMobile?: boolean
  position: 'start' | 'end'
  sectionId: string
  triggerValue: string
}

export function SectionIntersectionObserver({
  isMobile = false,
  position,
  sectionId,
  triggerValue,
}: Props): JSX.Element {
  const { dispatch } = useUI()
  const elementRef = useRef<HTMLDivElement | null>(null)
  const [observer, setObserver] = useState<IntersectionObserver>()

  // Calculate ViewPort rootMargin
  const rootMargin = useMemo<string>(() => {
    // For SSR and desktop
    if (typeof window === 'undefined' || !isMobile) return '-40% 0%'
    // For mobile, re-calculate rootMargin
    const topMargin = 146
    const bottomMargin = window.innerHeight - topMargin - 400
    return `-${topMargin}px 0% -${bottomMargin}px 0%`
  }, [isMobile, window])

  useEffect(() => {
    if (!elementRef.current || typeof observer !== 'undefined') return
    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting)
            dispatch({
              type: 'SET_CALENDAR_DATE',
              payload: dayjs(triggerValue).toDate(),
            })
        })
      },
      { rootMargin, threshold: 1 },
    )
    setObserver(intersectionObserver)
    intersectionObserver.observe(elementRef.current)
  }, [elementRef?.current, observer, rootMargin])

  // Destroy Observer upon unmount
  useEffect(() => {
    return () => {
      if (observer) {
        observer.disconnect()
      }
    }
  }, [observer])

  return (
    <div
      id={sectionId}
      ref={elementRef}
      style={{
        position: 'absolute',
        top: position === 'start' ? '4rem' : 'unset',
        bottom: position === 'end' ? '4rem' : 'unset',
        width: '1rem',
        height: '1rem',
      }}
    ></div>
  )
}
